import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, CheckCircle2, ChevronDown, Clock3, MapPin } from "lucide-react";
import { useT } from "@/content/landing";
import { useReducedMotion } from "./Reveal";
import localBusinessAudit from "@/assets/local-business-audit.jpg";
function WhatsAppIcon(props) {
  return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 5.834c-1.691 0-3.33-.483-4.74-1.395l-.339-.202-3.52 1.047 1.047-3.437-.222-.353a7.024 7.024 0 0 1-1.067-3.752c0-3.806 3.098-6.904 6.908-6.904 1.848 0 3.587.72 4.887 2.027a6.855 6.855 0 0 1 2.02 4.88c0 3.806-3.097 6.904-6.904 6.904m0-15.326A8.426 8.426 0 0 0 7.55 3.91 8.424 8.424 0 0 0 3.91 7.55a8.423 8.423 0 0 0-1.281 4.58c0 1.67.49 3.25 1.34 4.58L2 22l5.379-1.927a8.39 8.39 0 0 0 4.58 1.34 8.426 8.426 0 0 0 8.422-8.422 8.426 8.426 0 0 0-8.422-8.422" />
    </svg>;
}
const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
const easeOut = p => 1 - Math.pow(1 - clamp(p), 3);

/** Scroll progress (0..1) of a tall section while it is pinned. */
function useScrollProgress(ref, enabled) {
  const [progress, setProgress] = useState(enabled ? 0 : 1);
  useEffect(() => {
    if (!enabled) {
      setProgress(1);
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      setProgress(total <= 0 ? 1 : clamp(-rect.top / total));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref, enabled]);
  return progress;
}
function Row({
  icon,
  label,
  value,
  note,
  bars,
  highlight = false,
  reveal,
  children
}) {
  return <div className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition-none ${highlight ? "border-[#3B82F6]/25 ring-4 ring-[#3B82F6]/5" : "border-[#E8ECF1]"}`} style={{
    opacity: 0.2 + 0.8 * reveal,
    transform: `translateY(${(1 - reveal) * 14}px)`
  }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.14em] text-[#94A3B8] uppercase">
            {label}
          </p>
          <p className="font-display mt-1.5 text-base font-semibold text-[#0F172A] tabular-nums sm:text-lg">
            {value}
          </p>
        </div>
        <span className="mt-0.5 shrink-0 text-[#3B82F6]">{icon}</span>
      </div>
      {typeof bars === "number" && <div className="mt-3 flex gap-1" aria-hidden>
          {Array.from({
        length: 12
      }, (_, i) => <span key={i} className="h-4 w-1 rounded-full" style={{
        backgroundColor: i < Math.round(bars * reveal) ? "#3B82F6" : "#E8ECF1"
      }} />)}
        </div>}
      {children}
      <p className="mt-2.5 text-[13px] leading-relaxed text-[#94A3B8]">{note}</p>
    </div>;
}
export function Hero() {
  const t = useT();
  const p = t.hero.panel;
  const reduced = useReducedMotion();
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef, !reduced);

  // Story stages
  const intro = easeOut(progress / 0.18); // panel enters, copy lifts
  const stage = (from, to) => easeOut((progress - from) / (to - from));
  const r1 = Math.max(0.32, stage(0, 0.2));
  const r2 = stage(0.16, 0.44);
  const r3 = stage(0.42, 0.7);
  const scoreReveal = stage(0.55, 0.9);
  const score = Math.round(68 * (0.35 + 0.65 * scoreReveal));
  const scanned = clamp(progress / 0.8);
  const steps = [p.history.label, p.ai.label, p.local.label];
  const activeStep = r3 > 0.15 ? 2 : r2 > 0.15 ? 1 : 0;
  return <section id="top" ref={sectionRef} className="relative bg-[#FAFBFC] lg:h-[320vh]">
      <div className="lg:sticky lg:top-0 lg:flex lg:h-screen lg:items-center">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{
        backgroundImage: "radial-gradient(60rem 30rem at 50% -6rem, rgba(59,130,246,0.10), transparent 70%)"
      }} />

        <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-4 pt-28 pb-20 sm:px-6 sm:pt-32 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-16 lg:pt-24 lg:pb-10">
          {/* Narrative column */}
          <div className="max-w-xl" style={{
          transform: `translateY(${-intro * 10}px)`
        }}>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#E8ECF1] bg-white px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] text-[#94A3B8] uppercase">
              {t.hero.eyebrow}
            </p>

            <h1 className="font-display mt-6 text-4xl leading-[1.1] font-bold tracking-tight text-[#0F172A] sm:text-5xl lg:text-[3.4rem]">
              {t.hero.headline[0]}
              <br />
              <span className="text-[#3B82F6]">{t.hero.headline[1]}</span>
            </h1>

            <p className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg">
              {t.hero.subline}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/analyse" className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl bg-[#1F2937] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[#1F2937]/10 transition-all hover:bg-[#111827] hover:shadow-xl hover:shadow-[#1F2937]/15 focus-visible:ring-2 focus-visible:ring-[#1F2937] focus-visible:ring-offset-2 focus-visible:outline-none">
                <BarChart3 size={18} className="shrink-0" aria-hidden />
                {t.hero.cta.primary}
              </Link>

              <a href={t.hero.cta.whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl border border-[#22C55E]/50 bg-[#22C55E]/5 px-5 py-3 text-sm font-semibold text-[#15803D] transition-all hover:bg-[#22C55E]/10 focus-visible:ring-2 focus-visible:ring-[#22C55E] focus-visible:ring-offset-2 focus-visible:outline-none">
                <WhatsAppIcon className="h-[18px] w-[18px] shrink-0" />
                {t.hero.cta.whatsapp}
              </a>
            </div>
            <p className="mt-3 text-sm text-[#94A3B8]">{t.hero.reassurance}</p>

            {/* Story rail */}
            <ol className="mt-9 hidden gap-2 lg:grid" aria-hidden>
              {steps.map((label, i) => <li key={label} className="flex items-center gap-3">
                  <span className="h-1 w-10 rounded-full" style={{
                backgroundColor: i <= activeStep ? "#3B82F6" : "#E8ECF1"
              }} />
                  <span className="text-[11px] font-semibold tracking-[0.14em] uppercase" style={{
                color: i === activeStep ? "#0F172A" : "#CBD5E1"
              }}>
                    {label}
                  </span>
                </li>)}
            </ol>
          </div>

          {/* Diagnostic column — driven by scroll */}
          <div className="w-full" style={{
          transform: `translateY(${intro * -6}px)`
        }}>
            <div className="relative overflow-hidden rounded-3xl border border-[#E8ECF1] bg-[#E8ECF1]/40 p-4 sm:p-6">
              {/* scan line follows scroll */}
              <div aria-hidden className="pointer-events-none absolute inset-x-0 h-px bg-[#3B82F6]/40" style={{
              top: `${scanned * 100}%`
            }} />
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-display text-sm font-semibold text-[#0F172A]">{p.title}</p>
                <p className="text-xs text-[#94A3B8] tabular-nums">
                  {p.caption} · {Math.round(scanned * 100)}%
                </p>
              </div>

              <div className="relative mt-4 h-24 overflow-hidden rounded-2xl border border-white bg-white sm:h-28">
                <img src={localBusinessAudit} alt="Lokales Unternehmen während einer digitalen Bestandsaufnahme" width={1536} height={1024} className="h-full w-full object-cover object-[center_56%]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/10 via-transparent to-[#3B82F6]/10" aria-hidden />
                <span className="absolute bottom-3 left-3 rounded-full border border-white/70 bg-white/90 px-3 py-1 text-[10px] font-semibold tracking-wide text-[#475569] uppercase shadow-sm backdrop-blur-sm">
                  {p.history.label}
                </span>
              </div>

              <div className="mt-4 flex items-center gap-4 rounded-2xl border border-[#E8ECF1] bg-white p-4 text-left">
                <span className="font-display text-4xl font-bold text-[#3B82F6] tabular-nums">
                  {score}
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#0F172A]">{p.score.label}</p>
                  <p className="text-xs text-[#94A3B8]">{p.score.caption}</p>
                </div>
              </div>

              <div className="mt-3 grid gap-3">
                <Row icon={<Clock3 size={20} aria-hidden />} label={p.history.label} value={p.history.value} note={p.history.note} bars={9} reveal={r1} />
                <Row icon={<CheckCircle2 size={20} aria-hidden />} label={p.ai.label} value={p.ai.value} note={p.ai.note} highlight reveal={r2}>
                  <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#E8ECF1]" aria-hidden>
                    <div className="h-full rounded-full bg-[#3B82F6]" style={{
                    width: `${82 * r2}%`
                  }} />
                  </div>
                </Row>
                <Row icon={<MapPin size={20} aria-hidden />} label={p.local.label} value={p.local.value} note={p.local.note} reveal={r3}>
                  <div className="mt-3 flex items-center gap-3" aria-hidden>
                    <div className="flex -space-x-2">
                      {["#3B82F6", "#93C5FD", "#CBD5E1"].map((c, i) => <span key={c} className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[10px] font-semibold text-white" style={{
                      backgroundColor: c,
                      opacity: r3
                    }}>
                          {[1, 4, 9][i]}
                        </span>)}
                    </div>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E8ECF1]">
                      <div className="h-full rounded-full bg-[#93C5FD]" style={{
                      width: `${66 * r3}%`
                    }} />
                    </div>
                  </div>
                </Row>
              </div>
            </div>
          </div>
        </div>

        {/* scroll hint */}
        <div aria-hidden className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-[#94A3B8] lg:block" style={{
        opacity: clamp(1 - progress * 6)
      }}>
          <ChevronDown size={22} className="motion-safe:animate-bounce" />
        </div>
      </div>
    </section>;
}